import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { Appointment, CreateAppointmentRequest } from '@/types/appointment';

// Local schema for the form fields. startTime is a bare "HH:mm" string here;
// handleSubmit combines it with the date picker value to build the ISO datetime
// before forwarding to the API. Using the API schema directly would reject "HH:mm".
const formSchema = z.object({
  startTime: z.string().min(1, 'Please select a time'),
  duration: z.number().int().min(15).max(480),
});
type FormValues = z.infer<typeof formSchema>;

interface AppointmentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateAppointmentRequest) => void;
  appointment?: Appointment;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

const EMPTY_FORM: FormValues = {
  startTime: '',
  duration: 60,
};

export function AppointmentForm({ open, onOpenChange, onSubmit, appointment, isLoading, mode }: AppointmentFormProps) {
  const [date, setDate] = useState<Date>();
  const [timeInput, setTimeInput] = useState('');

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: EMPTY_FORM,
  });

  // Set form values when appointment changes (edit mode)
  useEffect(() => {
    if (appointment && mode === 'edit') {
      const appointmentDate = new Date(appointment.startTime);
      const hours = appointmentDate.getHours();
      const minutes = appointmentDate.getMinutes();

      // Round to nearest 15-minute interval
      const roundedMinutes = Math.round(minutes / 15) * 15;
      let adjustedHours = roundedMinutes >= 60 ? hours + 1 : hours;
      const finalMinutes = roundedMinutes >= 60 ? roundedMinutes - 60 : roundedMinutes;

      // Clamp to 6am-8pm range
      if (adjustedHours < 6) {
        adjustedHours = 6;
      } else if (adjustedHours > 20) {
        adjustedHours = 20;
      }

      const timeString = `${adjustedHours.toString().padStart(2, '0')}:${finalMinutes.toString().padStart(2, '0')}`;

      setDate(appointmentDate);
      setTimeInput(timeString);
      form.reset({
        startTime: timeString,
        duration: appointment.duration,
      });
    }
  }, [appointment, form, mode]);

  // Reset to a blank slate each time the dialog opens in create mode, so a
  // previous create/edit can't leave stale values behind.
  useEffect(() => {
    if (open && mode === 'create') {
      form.reset(EMPTY_FORM);
      setDate(undefined);
      setTimeInput('');
    }
  }, [open, mode, form]);

  const handleSubmit = (data: FormValues) => {
    if (date && data.startTime) {
      const [hours, minutes] = data.startTime.split(':');
      const appointmentDate = new Date(date);
      appointmentDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

      onSubmit({
        ...data,
        startTime: appointmentDate.toISOString(),
      });
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset();
      setDate(undefined);
      setTimeInput('');
    }
    onOpenChange(newOpen);
  };

  const isCreateMode = mode === 'create';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{isCreateMode ? 'Create mission slot' : 'Edit mission slot'}</DialogTitle>
          <DialogDescription>
            {isCreateMode ? 'Add a new time slot for a hero.' : 'Change the time or duration of this slot.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormItem>
                <FormLabel>Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn('w-full pl-3 text-left font-normal', !date && 'text-muted-foreground')}
                      >
                        {date ? format(date, 'PPP') : <span>Select date</span>}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return d < today;
                      }}
                      autoFocus
                    />
                  </PopoverContent>
                </Popover>
              </FormItem>

              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Time</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        setTimeInput(value);
                      }}
                      value={timeInput || field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Array.from({ length: 57 }, (_, i) => {
                          const totalMinutes = 6 * 60 + i * 15;
                          const hours = Math.floor(totalMinutes / 60);
                          const minutes = totalMinutes % 60;
                          if (hours > 20) return null;
                          const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                          return (
                            <SelectItem key={timeString} value={timeString}>
                              {timeString}
                            </SelectItem>
                          );
                        }).filter(Boolean)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duration</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value, 10))}
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                      <SelectItem value="180">3 hours</SelectItem>
                      <SelectItem value="240">4 hours</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading || !date || !form.getValues('startTime')}>
                {isLoading ? (isCreateMode ? 'Creating...' : 'Updating...') : isCreateMode ? 'Create' : 'Update'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
